import type { AckResult, AssistantPoint, LiveActionItem } from '@live-stream/shared'

/**
 * The in-meeting assistant's panel (Phase 10), the pure part: what counts
 * as new on the badge, how sure an action item reads, and what a refusal
 * says to the person who asked.
 */

/** Action items the person has not had on screen yet. */
export function unseenItems(items: readonly LiveActionItem[], seen: ReadonlySet<string>): number {
  return items.filter((i) => !seen.has(i.id)).length
}

/** Claude's confidence, as words: a task agreed, or one that may only have been mentioned. */
export function certainty(confidence: number): 'likely' | 'possible' {
  return confidence >= 0.75 ? 'likely' : 'possible'
}

export const POINT_LABEL: Record<AssistantPoint['kind'], string> = {
  point: 'Key point',
  decision: 'Decision',
  'for-you': 'For you',
}

/** The points of an answer, grouped by kind in the order a catch-up reads best. */
export function groupPoints(
  points: readonly AssistantPoint[],
): Array<{ kind: AssistantPoint['kind']; points: AssistantPoint[] }> {
  const order: AssistantPoint['kind'][] = ['for-you', 'decision', 'point']
  return order
    .map((kind) => ({ kind, points: points.filter((p) => p.kind === kind) }))
    .filter((g) => g.points.length > 0)
}

/** A failed ack as a sentence for the panel. The server's own words, where it gave some. */
export function failureText(result: Extract<AckResult<unknown>, { ok: false }>): string {
  switch (result.error.code) {
    case 'timeout':
      return 'The assistant took too long to answer. Try again.'
    case 'captions_off':
      return result.error.message || 'The assistant needs captions on.'
    default:
      return result.error.message || 'Something went wrong. Try again.'
  }
}
